import React, { useCallback, useMemo, useState } from 'react'
import CollapsibleSection from '../../components/CollapsibleSection/CollapsibleSection'
import WorldCupTeamFlag from './WorldCupTeamFlag'
import {
  CompactMatchScoreLine,
  ExactHitBadge,
  getGuessHits,
  hasMatchResult,
  MatchGuessListRow,
} from './WorldCupGuessScore'
import { buildWorldCupMatchListSections, groupWorldCupMatchesByDate } from './worldCupMatchSections'
import { countMissingGuesses, formatMissingGuessesLabel } from './worldCupMissingGuesses'

function fmtMatchTime(iso) {
  const d = new Date(iso)
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function fmtDateHeading(iso) {
  return new Date(iso).toLocaleDateString('da-DK', { day: 'numeric', month: 'long' })
}

function sortGuessesByPoints(guesses) {
  return [...guesses].sort((a, b) => {
    const pointsDiff = (b.pointsAwarded || 0) - (a.pointsAwarded || 0)
    if (pointsDiff !== 0) return pointsDiff
    return (a.userName || '').localeCompare(b.userName || '', 'da')
  })
}

function renderMatchScores({ match, mine, hasResult }) {
  const hits = mine ? getGuessHits(mine, match) : null

  if (hasResult && mine) {
    return (
      <>
        <CompactMatchScoreLine
          home={mine.homeGoals}
          away={mine.awayGoals}
          hits={hits}
          variant="guess"
        />
        <CompactMatchScoreLine
          home={match.homeGoals}
          away={match.awayGoals}
          hits={hits}
          variant="result"
        />
      </>
    )
  }
  if (hasResult) {
    return (
      <CompactMatchScoreLine
        home={match.homeGoals}
        away={match.awayGoals}
        hits={null}
        variant="result"
      />
    )
  }
  if (mine) {
    return (
      <CompactMatchScoreLine
        home={mine.homeGoals}
        away={mine.awayGoals}
        hits={null}
        variant="guess"
      />
    )
  }
  return <span className="worldcup-match-score" title="Ingen score endnu">-</span>
}

function renderMatchBlock({
  match,
  mine,
  isExpanded,
  selectedMatchGuesses,
  flagCodeByName,
  onOpenGuess,
  onOpenMatchGuesses,
}) {
  const isLocked = Date.now() >= new Date(match.kickoffTime).getTime()
  const canGuess = !mine && !isLocked
  const hasResult = hasMatchResult(match)
  const isFinished = match.status === 'finished'
  const isLive = match.status === 'live'
  const hits = mine ? getGuessHits(mine, match) : null
  const outcomeClass =
    isFinished && mine
      ? hits?.exactHit
        ? ' worldcup-match-row--perfect'
        : mine.pointsAwarded > 0
          ? ' worldcup-match-row--earned'
          : ' worldcup-match-row--miss'
      : ''

  return (
    <div key={match.id} className="worldcup-match-block">
      <div
        className={`worldcup-match-row${canGuess ? ' worldcup-match-row--clickable worldcup-match-row--needs-guess' : ''}${isLive ? ' worldcup-match-row--live' : ''}${mine?.isJoker ? ' worldcup-match-row--joker' : ''}${outcomeClass}`}
        onClick={canGuess ? () => onOpenGuess(match) : undefined}
      >
        <span className="worldcup-match-time">{fmtMatchTime(match.kickoffTime)}</span>
        <div className="worldcup-match-fixture">
          <span className="worldcup-match-team worldcup-match-team--home">
            <span className="worldcup-match-team-name">{match.homeTeam}</span>
            <WorldCupTeamFlag name={match.homeTeam} flagCode={flagCodeByName[match.homeTeam]} />
          </span>
          <div className="worldcup-match-scores">
            {renderMatchScores({ match, mine, hasResult })}
          </div>
          <span className="worldcup-match-team worldcup-match-team--away">
            <WorldCupTeamFlag name={match.awayTeam} flagCode={flagCodeByName[match.awayTeam]} />
            <span className="worldcup-match-team-name">{match.awayTeam}</span>
          </span>
        </div>
        <div className="worldcup-match-meta">
          {isFinished && hits?.exactHit ? <ExactHitBadge compact /> : null}
          {isFinished && mine ? (
            <span
              className={`worldcup-match-points worldcup-my-guess-points${
                mine.pointsAwarded > 0
                  ? ' worldcup-my-guess-points--earned'
                  : ' worldcup-my-guess-points--zero'
              }`}
            >
              {mine.pointsAwarded} pt
            </span>
          ) : null}
          {mine?.isJoker ? <span className="worldcup-match-joker">Joker</span> : null}
          {isLive ? <span className="worldcup-match-live">Live</span> : null}
          {canGuess ? (
            <>
              <span className="worldcup-match-needs-guess">Mangler gæt</span>
              <button
                type="button"
                className="worldcup-match-guess-btn"
                onClick={(e) => {
                  e.stopPropagation()
                  onOpenGuess(match)
                }}
              >
                Gæt
              </button>
            </>
          ) : null}
          {mine ? (
            <button
              type="button"
              className="worldcup-match-guesses-btn"
              onClick={(e) => {
                e.stopPropagation()
                onOpenMatchGuesses(match.id)
              }}
            >
              {isExpanded ? 'Skjul gæt' : 'Se gæt'}
            </button>
          ) : null}
        </div>
      </div>
      {isExpanded ? (
        <div className="worldcup-match-guesses">
          {!mine && !isFinished ? (
            <div className="worldcup-match-guesses-empty">Afgiv dit eget gæt for at se andres gæt.</div>
          ) : selectedMatchGuesses.length === 0 ? (
            <div className="worldcup-match-guesses-empty">Ingen gæt endnu.</div>
          ) : (
            sortGuessesByPoints(selectedMatchGuesses).map((g) => (
              <MatchGuessListRow
                key={g.id}
                guess={g}
                match={match}
                isFinished={isFinished}
              />
            ))
          )}
        </div>
      ) : null}
    </div>
  )
}

export default function WorldCupMatchesView({
  matches = [],
  stages,
  teams = [],
  myGuessByMatch = {},
  selectedMatchId,
  selectedMatchGuesses = [],
  onOpenGuess,
  onOpenMatchGuesses,
}) {
  const [collapsedSections, setCollapsedSections] = useState({})

  const flagCodeByName = useMemo(() => {
    const map = {}
    teams.forEach((t) => {
      if (t?.name) map[t.name] = t.flagCode
    })
    return map
  }, [teams])

  const matchListSections = useMemo(
    () => buildWorldCupMatchListSections(matches, stages),
    [matches, stages]
  )

  const toggleSectionCollapse = useCallback((sectionKey) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionKey]: !(prev[sectionKey] ?? true),
    }))
  }, [])

  if (matches.length === 0) {
    return <div className="worldcup-matches-empty">Ingen kampe endnu.</div>
  }

  return (
    <div className="worldcup-matches-list">
      {matchListSections.map((matchSection) => {
        const dateGroups = groupWorldCupMatchesByDate(matchSection.matches, fmtDateHeading)
        const isCollapsed = collapsedSections[matchSection.key] ?? true
        const missingGuesses = countMissingGuesses(matchSection.matches, myGuessByMatch)

        return (
          <CollapsibleSection
            key={matchSection.key}
            title={`${matchSection.title} (${matchSection.matches.length})`}
            isCollapsed={isCollapsed}
            onToggle={() => toggleSectionCollapse(matchSection.key)}
            headerBadge={
              missingGuesses > 0 ? (
                <span className="worldcup-matches-section-badge" title={`${missingGuesses} ${missingGuesses === 1 ? 'kamp mangler' : 'kampe mangler'} at gætte`}>
                  {formatMissingGuessesLabel(missingGuesses)}
                </span>
              ) : null
            }
          >
            <div className="worldcup-matches-section">
              {dateGroups.map((section, sectionIndex) => (
                <section key={section.dateKey} className="worldcup-matches-day">
                  <h4 className="worldcup-matches-date">{section.label}</h4>
                  <div className="worldcup-matches-day-list">
                    {section.matches.map((match) => renderMatchBlock({
                      match,
                      mine: myGuessByMatch[match.id],
                      isExpanded: selectedMatchId === match.id,
                      selectedMatchGuesses,
                      flagCodeByName,
                      onOpenGuess,
                      onOpenMatchGuesses,
                    }))}
                  </div>
                  {sectionIndex < dateGroups.length - 1 ? <hr className="worldcup-matches-divider" /> : null}
                </section>
              ))}
            </div>
          </CollapsibleSection>
        )
      })}
    </div>
  )
}
